import React from 'react';
import { FileText, Download, Play, AlertTriangle } from 'lucide-react';
import { MessageMedia, MessageType } from '../../../types/models/whatsapp';
import useMediaUrl from '../../../hooks/useMediaUrl';

interface MediaContentProps {
  type: MessageType;
  media: MessageMedia;
}

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Placeholder: React.FC<{ failed: boolean; className?: string }> = ({
  failed,
  className = 'h-40 w-56'
}) => (
  <div
    className={`flex items-center justify-center rounded-lg bg-gray-200 text-xs text-gray-500 ${className}`}
  >
    {failed ? (
      <span className="flex items-center gap-1.5 px-2 text-center">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-warning-600" />
        Media unavailable
      </span>
    ) : (
      <span className="animate-pulse">Loading…</span>
    )}
  </div>
);

/**
 * Media message body, with its own URL resolution.
 *
 * Split out of MessageBubble so that component can stay memoized and stateless:
 * only the messages that actually carry media pay for the signing round trip.
 */
const MediaContent: React.FC<MediaContentProps> = ({ type, media }) => {
  const { url, failed } = useMediaUrl(media.r2Key, media.url);

  const caption = media.caption ? (
    <p className="text-[15px] leading-snug">{media.caption}</p>
  ) : null;

  switch (type) {
    case 'image':
      return (
        <div className="space-y-1">
          {url ? (
            <a href={url} target="_blank" rel="noopener noreferrer">
              <img
                src={url}
                alt={media.caption || 'Image'}
                className="max-h-80 rounded-lg object-cover"
                loading="lazy"
              />
            </a>
          ) : (
            <Placeholder failed={failed} />
          )}
          {caption}
        </div>
      );

    case 'video':
      return (
        <div className="space-y-1">
          {url ? (
            <video
              src={url}
              controls
              preload="metadata"
              className="max-h-80 max-w-[18rem] rounded-lg"
            />
          ) : (
            <div className="relative flex h-40 w-56 items-center justify-center rounded-lg bg-gray-800">
              {failed ? (
                <AlertTriangle className="h-8 w-8 text-warning-400" />
              ) : (
                <Play className="h-10 w-10 text-white opacity-60" />
              )}
            </div>
          )}
          {caption}
        </div>
      );

    case 'audio':
      return url ? (
        <audio controls src={url} className="max-w-[16rem]">
          Audio message
        </audio>
      ) : (
        <Placeholder failed={failed} className="h-12 w-56" />
      );

    case 'sticker':
      return url ? (
        <img src={url} alt="Sticker" className="h-32 w-32" />
      ) : (
        <Placeholder failed={failed} className="h-32 w-32" />
      );

    case 'document':
    default:
      return (
        <a
          href={url || undefined}
          target="_blank"
          rel="noopener noreferrer"
          aria-disabled={!url}
          className={`flex items-center gap-3 rounded-lg bg-black/5 p-2 ${
            url ? '' : 'pointer-events-none opacity-60'
          }`}
        >
          <FileText className="h-8 w-8 shrink-0 text-primary-600" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium">
              {media.filename || 'Document'}
            </span>
            <span className="block text-xs text-gray-500">
              {failed ? 'Unavailable' : formatSize(media.size)}
            </span>
          </span>
          <Download className="h-4 w-4 shrink-0 text-gray-500" />
        </a>
      );
  }
};

export default MediaContent;
